// TelcoTrack - Topbar page title service
// Feature pages (via app-page-header or [ttTopbarPageTitle]) register titles.
// The shell uses the topmost title for the breadcrumb; the heading itself renders in the page body.

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { NavIconId } from '../../shared/utils/nav-icons';

export interface TopbarPageTitleEntry {
  ownerId: symbol;
  title: string;
  subtitle: string;
  icon: NavIconId | '';
}

export interface TopbarPageTitleState {
  title: string;
  subtitle: string;
  icon: NavIconId | '';
}

const EMPTY_STATE: TopbarPageTitleState = { title: '', subtitle: '', icon: '' };

@Injectable({
  providedIn: 'root',
})
export class TopbarPageTitleService {
  private readonly stack: TopbarPageTitleEntry[] = [];
  private readonly stateSubject = new BehaviorSubject<TopbarPageTitleState>(EMPTY_STATE);
  readonly state$ = this.stateSubject.asObservable();

  /**
   * Register or update a title. Last registered entry wins (supports nested detail pages).
   * Optional `subtitle` is shown under the title in the global topbar (e.g. polygon tab context).
   */
  setTitle(
    ownerId: symbol,
    title: string,
    icon: NavIconId | '' = '',
    subtitle = '',
  ): void {
    const trimmed = (title ?? '').trim();
    const trimmedSubtitle = (subtitle ?? '').trim();
    const idx = this.stack.findIndex((e) => e.ownerId === ownerId);
    if (!trimmed) {
      if (idx >= 0) {
        this.stack.splice(idx, 1);
        this.emit();
      }
      return;
    }
    if (idx >= 0) {
      this.stack[idx].title = trimmed;
      this.stack[idx].subtitle = trimmedSubtitle;
      this.stack[idx].icon = icon;
    } else {
      this.stack.push({ ownerId, title: trimmed, subtitle: trimmedSubtitle, icon });
    }
    this.emit();
  }

  clearTitle(ownerId: symbol): void {
    const idx = this.stack.findIndex((e) => e.ownerId === ownerId);
    if (idx < 0) return;
    this.stack.splice(idx, 1);
    this.emit();
  }

  clearAll(): void {
    if (this.stack.length === 0) return;
    this.stack.length = 0;
    this.emit();
  }

  private emit(): void {
    const top = this.stack[this.stack.length - 1];
    this.stateSubject.next(
      top ? { title: top.title, subtitle: top.subtitle, icon: top.icon } : EMPTY_STATE,
    );
  }
}
